import { Button, Dropdown } from 'components/core';
import { buildQuantityOptions } from 'components/ProductDetails';
import { useI18n } from 'contexts/i18n';
import { FC, useEffect, useState } from 'react';
import { DownloadAgreementModal } from 'components';
import { MaterialTypeEnum } from 'types';
import { CustomizedProductDetailsProps, Styled } from '.';
import { useAddLineItem } from 'reactQuery/mutations';
import { AddToCartConfirmationModal } from 'components/AddToCartConfirmationModal';

const CustomizedProductDetails: FC<CustomizedProductDetailsProps> = ({
  product,
  projectId,
  onCloseEditor,
  mode,
  isProjectEdited,
  isLowResolutionImagesPresent,
  isVideo,
  hasMultiplePages,
}) => {
  const addLineItemMutation = useAddLineItem();
  const [showAgreementConfirmationModal, setShowAgreementConfirmationModal] =
    useState(false);
  const [showAddToCartConfirmation, setShowAddToCartConfirmation] =
    useState(false);

  const quantityOptions = buildQuantityOptions(product?.quantityLimit);
  const [quantity, setQuantity] = useState(quantityOptions[0]?.value);

  useEffect(() => {
    if (quantityOptions.length > 0) {
      setQuantity(quantityOptions[0].value);
    }
  }, [quantityOptions[0]?.value]);

  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);

  const i18n = useI18n();
  if (!product) {
    return <></>;
  }

  const isDownloadable =
    product.materialType === MaterialTypeEnum.digital ||
    product.materialType === MaterialTypeEnum.digital_physical;

  const isOrderable =
    product.materialType === MaterialTypeEnum.physical ||
    product.materialType === MaterialTypeEnum.digital_physical;

  const handleAddToCart = () => {
    if (quantity) {
      const qty = parseInt(quantity);
      setIsAddedToCart(true);
      addLineItemMutation.mutate({
        variantId: product.variantId,
        quantity: qty,
        setShowAddToCartConfirmation,
        projectId: projectId,
      });
    } else {
      alert('quantity is required');
    }
  };

  const handleDownload = () => {
    setShowAgreementConfirmationModal(true);
  };

  return (
    <Styled.Section>
      <Styled.ProductName>{product.name}</Styled.ProductName>
      <Styled.ProductSKU>{i18n['SKU']} {product.sku}</Styled.ProductSKU>
      {isOrderable && (
        <Styled.ProductPrice>{product.displayPrice}</Styled.ProductPrice>
      )}

      <Styled.AddToCartSection>
        {isOrderable && (
          <>
            <Dropdown
              options={quantityOptions}
              width="50%"
              onChange={val => {
                setQuantity(val);
              }}
              selectedValue={quantityOptions[0]?.value}
              label={i18n['Qty.']}
            ></Dropdown>
            <Button
              variant="primary"
              disabled={
                !product.inStock ||
                quantity === '0' ||
                (mode === 'create' && !isProjectEdited) ||
                isLowResolutionImagesPresent
              }
              onClick={() => handleAddToCart()}
              width="50%"
              loading={addLineItemMutation.isLoading}
            >
              {product.inStock ? i18n['Add to cart'] : i18n['Out of Stock']}
            </Button>
          </>
        )}

        {isDownloadable && !isDownloaded && (
          <Button
            variant={isOrderable ? 'secondary' : 'primary'}
            onClick={handleDownload}
            width="50%"
            disabled={
              (mode === 'create' && !isProjectEdited) ||
              isLowResolutionImagesPresent
            }
          >
            {i18n['Download']}
          </Button>
        )}
      </Styled.AddToCartSection>

      {showAgreementConfirmationModal && (
        <DownloadAgreementModal
          product={product}
          projectId={projectId}
          onClose={() => {
            setShowAgreementConfirmationModal(false);
          }}
          closeEditor={onCloseEditor}
          source="ProductPage"
          isAddedToCart={isAddedToCart}
          isDownloaded={isDownloaded}
          setIsDownloaded={setIsDownloaded}
          isVideo={isVideo}
          hasMultiplePages={hasMultiplePages}
        />
      )}
      {showAddToCartConfirmation && (
        <AddToCartConfirmationModal
          name={product.name}
          price={product.displayPrice}
          imageUrl={product.images[0].thumbnail}
          onClose={() => {
            setShowAddToCartConfirmation(false);
            onCloseEditor();
          }}
          showDownloadButton={
            product.materialType === MaterialTypeEnum.digital_physical &&
            !isDownloaded
          }
          onDownload={() => {
            setShowAddToCartConfirmation(false);
            handleDownload();
          }}
        />
      )}
    </Styled.Section>
  );
};

export default CustomizedProductDetails;
